/* =============================================================================
   VIEWS / TAKEAWAY-FORM — Bestell-Modal "Zum Mitnehmen" (v0.19.4)
   =============================================================================
   Eigenständiges Pop-Up für den Abhol-Bestellflow. Wird vom Service-Type-
   Toggle im Reservierungsformular per Klick auf "Zum Mitnehmen" geöffnet.
   Das Reservierungsformular selbst bleibt komplett unangetastet — wir
   legen das Modal nur als Overlay darüber.

   Inhalt:
     - Header (Titel + Schließen)
     - Eingebetteter menu-picker (eigener Root-Container 'takeaway-menu-root')
     - Kontaktdaten: Name, Telefon, E-Mail
     - Abholdatum + Abholzeit
     - Footer mit Gesamtbetrag + Abbrechen/Bestellen-Buttons

   Submit: createCustomer mit serviceType='takeaway'. Bei Erfolg Modal
   schließen, Toggle auf "Zu Gast" zurücksetzen, gemeinsame Success-Box
   des Reservierungsformulars anzeigen.
============================================================================ */

import { htmlEscape, fmtEUR, todayISO, showToast } from '../utils/helpers.js';
import { generateTimeSlots } from '../core/reservation-engine.js';
import {
  initMenuPicker,
  getOrder as getMenuOrder,
  validateOrder as validateMenuOrder
} from './menu-picker.js';
// S234 T3 — Stripe-Checkout-Flow zentral (Single-Source für Takeaway + Lieferung).
import { injectStripeCheckoutBlock } from '../core/stripe-checkout.js';
// S256 — dieselbe Übersetzung wie im Tisch-Pfad: nur Kennungen und Mengen.
import { lineToOrderRpcItem, friendlyOrderError } from './menu-picker-helpers.js';
// S266 — Gast-Kanäle: Server-Ablehnung erkennen (CHANNEL_DISABLED:takeaway).
import { channelDisabledKeyFromError } from '../core/guest-channels.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

function currentRestaurantIdLazy() {
  if (window.App && typeof window.App.getCurrentRestaurantId === 'function') {
    return window.App.getCurrentRestaurantId();
  }
  return (window.AppConfig && window.AppConfig.defaultRestaurantId) || 'rest_default';
}

let _escBound = false;
function bindEscOnce() {
  if (_escBound || typeof document === 'undefined') return;
  _escBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.getElementById('takeaway-modal-overlay')) {
      e.preventDefault();
      closeTakeawayModal();
    }
  });
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/** Öffnet das Bestell-Modal. Wird vom service-toggle aufgerufen. */
export function openTakeawayModal() {
  // Schon offen? Nichts tun.
  if (document.getElementById('takeaway-modal-overlay')) return;

  bindEscOnce();

  const state = getState();
  const brand = (state.settings && state.settings.restaurantName) || 'Grill & Green';

  // Slot-Times aus dem Engine — selbe Liste wie das Reservierungsformular.
  // UX-Win S24: Slot-Filter angewendet, damit Slots < now()+orderLeadTimeMin
  // gar nicht erst angeboten werden (RPC-Error P0001 vermeiden).
  const initialDate = todayISO();
  const { options: slotOptions, defaultTime, earliestLabel } = renderTakeawayTimeOptions(initialDate, state.settings);

  const overlayHTML = `
    <div id="takeaway-modal-overlay" class="ta-modal-overlay" onclick="if(event.target===this){closeTakeawayModal();}">
      <div class="ta-modal" role="dialog" aria-modal="true" aria-labelledby="ta-modal-title">
        <div class="ta-modal-header">
          <div class="ta-modal-head-text">
            <div id="ta-modal-title" class="ta-modal-title">Zum Mitnehmen bestellen</div>
            <div class="ta-modal-subtitle">
              Vorbestellen und entspannt im <strong>${htmlEscape(brand)}</strong> abholen.
            </div>
          </div>
          <button type="button" class="ta-modal-close"
                  onclick="closeTakeawayModal()" aria-label="Schließen">✕</button>
        </div>

        <div class="ta-modal-body">
          <!-- 1. Schritt: Speisekarte mit Warenkorb -->
          <section class="ta-section">
            <h3 class="ta-section-head">1. Wähle deine Gerichte</h3>
            <div id="takeaway-menu-root"></div>
          </section>

          <!-- 2. Schritt: Abholzeit -->
          <section class="ta-section">
            <h3 class="ta-section-head">2. Wann möchtest du abholen?</h3>
            <div class="field-row">
              <div class="field">
                <label for="ta-date">Abholdatum *</label>
                <input id="ta-date" type="date" required>
              </div>
              <div class="field">
                <label for="ta-time">Abholzeit *</label>
                <select id="ta-time" required>${slotOptions}</select>
                <div id="ta-time-hint" class="hint" style="margin-top:6px;color:var(--ink-muted, #6b7280);font-size:12px;">
                  ${earliestLabel ? htmlEscape(earliestLabel) : ''}
                </div>
              </div>
            </div>
          </section>

          <!-- 3. Schritt: Kontakt -->
          <section class="ta-section">
            <h3 class="ta-section-head">3. Deine Kontaktdaten</h3>
            <div class="field">
              <label for="ta-name">Vollständiger Name *</label>
              <input id="ta-name" type="text" required autocomplete="name">
            </div>
            <div class="field-row">
              <div class="field">
                <label for="ta-phone">Telefonnummer *</label>
                <input id="ta-phone" type="tel" required autocomplete="tel">
              </div>
              <div class="field">
                <label for="ta-email">E-Mail-Adresse *</label>
                <input id="ta-email" type="email" required autocomplete="email">
              </div>
            </div>
            <div class="field" id="ta-gdpr-field" style="display:none;">
              <label class="checkbox">
                <input type="checkbox" id="ta-gdpr">
                <span>Ich willige ein, dass meine Daten zur Bearbeitung der Bestellung verarbeitet werden. <span id="ta-gdpr-link"></span></span>
              </label>
            </div>
            <!-- S226 — Newsletter-Abo (Double-Opt-in), UNVORAUSGEWÄHLT. -->
            <div class="field" id="ta-newsletter-field">
              <label class="checkbox">
                <input type="checkbox" id="ta-newsletter">
                <span>Ja, ich möchte den Newsletter abonnieren. Ich erhalte eine Bestätigungsmail und kann mich jederzeit kostenlos abmelden.</span>
              </label>
            </div>
          </section>
        </div>

        <div class="ta-modal-footer">
          <div class="ta-modal-actions">
            <button type="button" class="btn btn-ghost"
                    onclick="closeTakeawayModal()">Abbrechen</button>
            <button type="button" class="btn btn-primary"
                    onclick="submitTakeawayOrder()">Bestellung absenden</button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', overlayHTML);
  document.body.style.overflow = 'hidden';

  // Default-Datum auf heute, Default-Zeit auf 19:00.
  const d = document.getElementById('ta-date');
  if (d) { d.min = todayISO(); d.value = todayISO(); }
  const t = document.getElementById('ta-time');
  if (t && defaultTime) t.value = defaultTime;

  // UX-Win S24: bei Datums-Wechsel die Slot-Liste neu rendern. Damit
  // verschwinden Slots, die fuer "heute" zu frueh waren, sobald der User
  // auf einen spaeteren Tag wechselt, und umgekehrt.
  if (d) {
    d.addEventListener('change', () => {
      const st = getState();
      const { options, defaultTime: dt, earliestLabel: lbl } =
        renderTakeawayTimeOptions(d.value, st.settings);
      const sel = document.getElementById('ta-time');
      const hint = document.getElementById('ta-time-hint');
      if (sel) {
        const prev = sel.value;
        sel.innerHTML = options;
        // Wenn die vorher gewaehlte Zeit noch im Set ist, sie behalten.
        const stillThere = Array.from(sel.options).some(o => o.value === prev);
        sel.value = stillThere ? prev : (dt || sel.options[0]?.value || '');
      }
      if (hint) hint.textContent = lbl || '';
    });
  }

  // GDPR-Block je nach Settings.
  if (state.settings && state.settings.requireGdprConsent) {
    const g = document.getElementById('ta-gdpr-field');
    if (g) g.style.display = 'block';
    const link = document.getElementById('ta-gdpr-link');
    if (link && state.settings.gdprPolicyUrl) {
      link.innerHTML = `<a href="${htmlEscape(state.settings.gdprPolicyUrl)}" target="_blank" rel="noopener">Mehr erfahren</a>`;
    }
  }

  // menu-picker mit eigenem Root mounten — frische Bestellung.
  initMenuPicker('takeaway-menu-root');
}

/** Schließt das Bestell-Modal. opts.silent=true verhindert das Zurücksetzen
 *  des Toggle-Chips (wird beim Klick auf "Zu Gast" aus dem Toggle selbst
 *  übergeben — sonst Endlos-Schleife). */
export function closeTakeawayModal(opts) {
  const overlay = document.getElementById('takeaway-modal-overlay');
  if (overlay) overlay.remove();
  document.body.style.overflow = '';

  // menu-picker-Root des Hauptformulars wieder als Default setzen, damit
  // die normale Vorbestellung (Dine-In) weiter funktioniert.
  if (typeof window.initMenuPicker === 'function') {
    // initMenuPicker greift nur, wenn der Root im DOM existiert. Im Dine-In-
    // Flow ist '#menu-picker-root' versteckt bis der User die Vorbestellung
    // wieder aktiviert; dann wird sie ohnehin neu initialisiert.
    try { window.initMenuPicker('menu-picker-root'); } catch (_) {}
  }

  if (!opts || !opts.silent) {
    if (typeof window._resetServiceTypeToggleSilently === 'function') {
      window._resetServiceTypeToggleSilently();
    }
  }
}

/**
 * Liefert den MwSt-Satz für ein menu_item — Speisen 7% (Takeaway-Regel),
 * Getränke 19%. Settings-Overrides (taxRateFood/taxRateDrinks) werden
 * mitgenommen, falls vom Admin angepasst.
 */
function deriveTaxRate(menuItem, settings) {
  const food   = Number((settings && settings.taxRateFood)   || 7);
  const drinks = Number((settings && settings.taxRateDrinks) || 19);
  const itemType = (menuItem && menuItem.itemType) || 'main';
  return itemType === 'drink' ? drinks : food;
}

/** Wandelt eine Bestellzeile aus menu-picker (v2-Format) in die RPC-
 *  konforme Form für create_customer_order. Cent-Preis pro Stück wird
 *  aus lineTotal/qty abgeleitet (rundet auf vollen Cent). */

/** Submit-Handler. Liest alle Felder + Warenkorb, validiert, ruft die
 *  RPC create_customer_order über App.storage.orders.createCustomer.
 *  Bei Erfolg: Modal schließen, Toggle resetten, Erfolgsbox anzeigen. */
export async function submitTakeawayOrder() {
  const state = getState();

  const name  = (document.getElementById('ta-name')?.value || '').trim();
  const phone = (document.getElementById('ta-phone')?.value || '').trim();
  const email = (document.getElementById('ta-email')?.value || '').trim();
  const date  =  document.getElementById('ta-date')?.value || '';
  const time  =  document.getElementById('ta-time')?.value || '';

  if (!name)  return showToast('Bitte deinen Namen eingeben.', 'error');
  if (!phone) return showToast('Bitte deine Telefonnummer eingeben.', 'error');
  if (!email) return showToast('Bitte deine E-Mail-Adresse eingeben.', 'error');
  if (!date)  return showToast('Bitte ein Abholdatum wählen.', 'error');
  if (!time)  return showToast('Bitte eine Abholzeit wählen.', 'error');
  if (date < todayISO()) return showToast('Das Abholdatum darf nicht in der Vergangenheit liegen.', 'error');

  if (state.settings && state.settings.requireGdprConsent) {
    const consent = document.getElementById('ta-gdpr');
    if (!consent || !consent.checked) {
      return showToast('Bitte stimme den Datenschutzbestimmungen zu.', 'error');
    }
  }

  // Warenkorb prüfen — muss mind. 1 Item enthalten + Pflicht-Optionen ok.
  const vr = validateMenuOrder();
  if (!vr.ok) {
    return showToast(vr.errors[0] || 'Bitte vervollständige deine Bestellung.', 'error');
  }
  const order = getMenuOrder();
  // S169 P4 — Deal-Positionen → echte order_items (Decomposition).
  const dealItems = (typeof window.getDealOrderItems === 'function') ? window.getDealOrderItems() : [];
  if (!order || !Array.isArray(order.items) || (order.items.length === 0 && dealItems.length === 0)) {
    return showToast('Bitte mindestens ein Gericht auswählen.', 'error');
  }

  // Items aus dem menu-picker v2-Format in das RPC-Schema mappen.
  // S256 — Deal-Zeilen werden NICHT mehr im Client zerlegt: `lineToRpcItem`
  // reicht das `deal`-Objekt durch, und `price_order_line` macht daraus
  // server-seitig Hauptgericht und Getränk. `dealItems` bleibt nur noch als
  // Prüfung, ob überhaupt etwas ausgewählt ist.
  const menuList = Array.isArray(state.menu) ? state.menu : [];
  const items = order.items.map(line => lineToOrderRpcItem(line, menuList, state.settings)).filter(Boolean);

  const req = {
    restaurantId:     currentRestaurantIdLazy(),
    name, phone, email,
    pickupDate:       date,
    pickupTime:       time,
    items,
    preorderSnapshot: order,
    notes:            null,
    specialRequests:  order.specialRequests || null,
    gdprConsentAt:    (state.settings && state.settings.requireGdprConsent) ? new Date().toISOString() : null
  };

  let created;
  try {
    created = await window.App.storage.orders.createCustomer(req);
  } catch (err) {
    // S256 — Code und Meldung ins Log, ein verstaendlicher Satz an den Gast.
    console.error('[submitTakeawayOrder] orders.createCustomer:',
      (err && (err.code || '')) || '', err);
    showToast(friendlyOrderError(err), 'error');
    // S266 — Kanal „Mitnehmen" wurde geschlossen, während das Modal offen war:
    // Settings neu laden, Modal zu, Chip verschwindet (refreshGuestChannels).
    if (channelDisabledKeyFromError(err) && typeof window.refreshGuestChannels === 'function') {
      window.refreshGuestChannels().catch(() => {});
    }
    return;
  }

  // S226 — Newsletter-Abo (Double-Opt-in) NICHT-FATAL: Häkchen JETZT lesen,
  // bevor closeTakeawayModal() das Modal-DOM entfernt. Fehler blockieren die
  // Bestellung nie. Quelle web_order (Takeaway/Abholung).
  if (typeof window.maybeSubscribeFromCheckbox === 'function') {
    window.maybeSubscribeFromCheckbox('ta-newsletter', req.restaurantId, req.email, 'web_order');
  }

  // Event-Bus informieren. Mail-Versand für Order-Templates kommt in Phase 3
  // (Migration der email-templates auf die order-Pipeline). Bis dahin sieht
  // der Kunde nur die Erfolgsbox im UI.
  if (window.App.events) {
    try { await window.App.events.emit('order:created', created); } catch (e) {}
  }

  // Modal schließen und Erfolg anzeigen.
  closeTakeawayModal();

  const successWrap   = document.getElementById('customer-success');
  const successMsgEl  = document.getElementById('success-message');
  const formWrapper   = document.getElementById('customer-form-wrapper');
  if (successMsgEl) {
    const num = created && created.orderNumber ? ` (Nr. ${created.orderNumber})` : '';
    successMsgEl.textContent =
      `Danke für deine Bestellung${num}! Wir bereiten alles vor und melden uns per E-Mail, ` +
      `sobald deine Abholung um ${time} Uhr bereit ist.`;
  }
  if (formWrapper) formWrapper.style.display = 'none';
  if (successWrap) successWrap.style.display = 'block';

  // S74 Konzept-Cut: Confirmation-Screen ersetzt die received-Mail. Cancel-Link
  // + Hinweis prominent anzeigen, damit Customer ohne Mail-Backup nicht den
  // Storno-Pfad verliert. Datum/Zeit/Bestellnummer stehen schon im success-message-Text.
  try {
    const hint     = document.getElementById('success-confirmation-hint');
    const linkWrap = document.getElementById('success-cancel-link-wrap');
    const linkEl   = document.getElementById('success-cancel-link');
    if (hint) hint.style.display = '';
    const tok = created?.cancelToken || created?.cancel_token;
    if (linkWrap && linkEl && tok) {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const href = `${origin}/index.html#cancel=${tok}`;
      linkEl.href = href;
      linkEl.textContent = href;
      linkWrap.style.display = '';
    }
  } catch (e) {
    console.warn('[takeaway-form] Confirmation-Screen-Cancel-Link konnte nicht gefüllt werden:', e);
  }

  // S234 T3 — Stripe-Insel (geteilter Helper, Kanal 'takeaway'). Merkt die Order
  // zusätzlich lokal (Return-UX/Status-Seite).
  injectStripeCheckoutBlock({
    successBox: document.querySelector('#customer-success .success-box'),
    order: created,
    channel: 'takeaway'
  });
}

// -----------------------------------------------------------------------------
// Slot-Filter Helper (UX-Win S24)
// -----------------------------------------------------------------------------
// Filtert die generateTimeSlots()-Liste so, dass bei Datum=heute nur Slots
// >= now() + orderLeadTimeMin uebrig bleiben. Verhindert, dass der Gast einen
// Slot waehlt, den der RPC create_customer_order anschliessend mit P0001
// (lead-time-Verletzung) abweisen wuerde.
//
// Rueckgabe:
//   - options:        HTML-String fuer <select>-Innenhalt
//   - defaultTime:    sinnvolle Default-Auswahl (heute: erster verfuegbarer
//                     Slot; sonst: 19:00 falls vorhanden, sonst der erste)
//   - earliestLabel:  String "Frueheste Abholzeit: HH:MM" wenn heute, sonst ''
function renderTakeawayTimeOptions(dateISO, settings) {
  const slots = generateTimeSlots() || [];
  const isToday = dateISO === todayISO();
  const leadMin = Number((settings && settings.orderLeadTimeMin) || 30);

  let allowed = slots.slice();
  let earliestLabel = '';

  if (isToday) {
    const now = new Date();
    const minMinutes = now.getHours() * 60 + now.getMinutes() + leadMin;
    allowed = slots.filter(t => {
      const [hh, mm] = t.split(':').map(Number);
      const slotMin = (hh || 0) * 60 + (mm || 0);
      return slotMin >= minMinutes;
    });
    if (allowed.length > 0) {
      earliestLabel = `Frühestens ab ${allowed[0]} Uhr abholbar (${leadMin} Min Vorlauf).`;
    } else {
      earliestLabel = 'Heute keine Abholzeit mehr verfügbar — bitte morgen wählen.';
    }
  }

  const options = allowed.map(t =>
    `<option value="${htmlEscape(t)}">${htmlEscape(t)} Uhr</option>`
  ).join('');

  let defaultTime = '';
  if (isToday) {
    defaultTime = allowed[0] || '';
  } else {
    defaultTime = allowed.includes('19:00') ? '19:00' : (allowed[0] || '');
  }

  return { options, defaultTime, earliestLabel };
}

// Backward-Compat: window-Exposes für inline-onclick-Handler im Modal-HTML.
if (typeof window !== 'undefined') {
  Object.assign(window, {
    openTakeawayModal,
    closeTakeawayModal,
    submitTakeawayOrder
    // startStripeCheckout wird jetzt von core/stripe-checkout.js window-gebunden.
  });
}

// -----------------------------------------------------------------------------
// Test-Hook-Exports (additiv, zur Laufzeit ein No-Op).
// Pattern aus datev-export.js + day-close.js + orders.js + settings.js
// (Memory-Lesson feedback_gg_hard_way: sauberer Test-Vertrag statt
// Reflection auf private Symbols).
// -----------------------------------------------------------------------------
export {
  deriveTaxRate                as _deriveTaxRate,
  renderTakeawayTimeOptions    as _renderTakeawayTimeOptions
};
